import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Gauge,
  Activity,
  AlertTriangle,
  Battery,
  Cpu,
  Settings,
  Plug,
  Shield,
  Zap,
  History,
  Car,
  Power,
  Bluetooth,
  Wrench,
  Radio,
  Cable,
  FileText,
  Radar
} from "lucide-react";

// Pages
import Dashboard from "./pages/Dashboard";
import Diagnostics from "./pages/Diagnostics";
import LiveData from "./pages/LiveData";
import Modules from "./pages/Modules";
import Calibration from "./pages/Calibration";
import SafetyInfo from "./pages/SafetyInfo";
import SessionHistory from "./pages/SessionHistory";
import ServiceRoutines from "./pages/ServiceRoutines";
import UnifiedDashboard from "./pages/UnifiedDashboard";
import CANDecoder from "./pages/CANDecoder";
import WiringMap from "./pages/WiringMap";
import ServiceProcedures from "./pages/ServiceProcedures";
import MCUScanner from "./pages/MCUScanner";

// Components
import ServiceModeGuide from "./components/ServiceModeWizard";

// BLE Manager
import { bleManager, BLEManager } from "./config/bleConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = ({ connectionStatus, vehicleInfo, bleConnected, serviceModeStatus }) => {
  const location = useLocation();
  
  const navItems = [
    { path: "/", icon: Radio, label: "Command Center", highlight: true },
    { path: "/can-decoder", icon: Activity, label: "CAN Decoder", highlight: true },
    { path: "/wiring", icon: Cable, label: "Wiring Map", highlight: true },
    { path: "/procedures", icon: FileText, label: "Procedures", highlight: true },
    { path: "/scanner", icon: Radar, label: "MCU Scanner", highlight: true },
    { path: "/dashboard", icon: Gauge, label: "Overview" },
    { path: "/service-routines", icon: Wrench, label: "Service Routines" },
    { path: "/diagnostics", icon: AlertTriangle, label: "Diagnostics" },
    { path: "/live-data", icon: Activity, label: "Live Data" },
    { path: "/modules", icon: Cpu, label: "Modules" },
    { path: "/safety", icon: Shield, label: "Safety Info" },
    { path: "/history", icon: History, label: "History" },
  ];

  return (
    <nav className="nav-sidebar">
      <div className="nav-logo">
        <div className="nav-logo-icon">
          <Car size={28} className="text-white" />
        </div>
        <div>
          <h1 className="font-heading text-xl font-bold tracking-wider text-white">TESLA DIAG</h1>
          <p className="text-xs text-white/50 font-mono">MODEL S 2013</p>
        </div>
      </div>

      {/* Connection Status */}
      <div className={`connection-indicator mb-4 ${connectionStatus.connected ? 'connected' : 'disconnected'}`}>
        <span className={`connection-dot ${connectionStatus.connected ? 'connected' : 'disconnected'}`}></span>
        <span>{connectionStatus.connected ? 'MCU CONNECTED' : 'DISCONNECTED'}</span>
      </div>

      {/* ESP32 BT Status */}
      <div className={`connection-indicator mb-4 ${bleConnected ? 'connected' : 'disconnected'}`} style={{ background: bleConnected ? 'rgba(0,122,255,0.1)' : 'rgba(255,255,255,0.05)' }}>
        <Bluetooth size={14} className={bleConnected ? 'text-[#007AFF]' : 'text-white/40'} />
        <span className="text-xs">{bleConnected ? 'ESP32 BT Connected' : 'ESP32 BT Ready'}</span>
      </div>

      {/* Service Mode Guide Link */}
      <NavLink
        to="/service-guide"
        className={({ isActive }) => `w-full mb-6 p-3 glass-card hover:bg-white/10 transition-colors text-left block no-underline ${isActive ? 'bg-white/10' : ''}`}
        data-testid="open-service-wizard"
      >
        <div className="flex items-center gap-2">
          <Settings size={16} className={serviceModeStatus.active ? 'text-[#30D158]' : 'text-[#FF9F0A]'} />
          <span className="text-xs uppercase tracking-wider text-white/80">
            {serviceModeStatus.active ? 'Service Mode Active' : 'Service Mode Guide'}
          </span>
        </div>
        {serviceModeStatus.gatewayUnlocked && (
          <p className="text-xs text-[#30D158] mt-1">Gateway Unlocked</p>
        )}
      </NavLink>

      {/* Vehicle Info */}
      {connectionStatus.connected && vehicleInfo && (
        <div className="mb-6 p-3 glass-card">
          <p className="text-xs text-white/50 uppercase tracking-wider mb-1">VIN</p>
          <p className="font-mono text-sm text-[#007AFF]">{vehicleInfo.vin}</p>
          <p className="text-xs text-white/50 uppercase tracking-wider mt-2 mb-1">Firmware</p>
          <p className="font-mono text-sm text-white/80">{vehicleInfo.firmware_version}</p>
        </div>
      )}

      {/* Navigation Links */}
      <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `nav-link ${isActive ? 'active' : ''} ${item.highlight && !isActive ? 'text-[#007AFF]' : ''}`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
          >
            <item.icon size={20} />
            <span>{item.label}</span>
            {item.highlight && <span className="ml-auto text-xs bg-[#007AFF]/20 text-[#007AFF] px-1.5 py-0.5 rounded">NEW</span>}
          </NavLink>
        ))}
      </div>

      {/* Service Mode Indicator */}
      {connectionStatus.connected && serviceModeStatus.active && (
        <div className="service-mode rounded p-3 mt-4">
          <div className="flex items-center gap-2 text-[#30D158]">
            <Zap size={16} />
            <span className="font-heading text-sm uppercase tracking-wider">Service Mode Plus</span>
          </div>
        </div>
      )}
      
      {connectionStatus.connected && !serviceModeStatus.active && (
        <div className="service-mode rounded p-3 mt-4">
          <div className="flex items-center gap-2 text-[#FF3B30]">
            <AlertTriangle size={16} />
            <span className="font-heading text-sm uppercase tracking-wider">Limited Mode</span>
          </div>
        </div>
      )}
    </nav>
  );
};

function App() {
  const [connectionStatus, setConnectionStatus] = useState({ connected: false });
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [bleConnected, setBleConnected] = useState(false);
  const [bleLiveData, setBleLiveData] = useState(null);
  const [showServiceWizard, setShowServiceWizard] = useState(false);
  const [serviceModeStatus, setServiceModeStatus] = useState({
    active: false,
    gatewayUnlocked: false,
    fakraConnected: false,
    bleConnected: false
  });

  // Check connection status on mount
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  // Setup BLE callbacks
  useEffect(() => {
    bleManager.onData((data) => {
      setBleLiveData(data);
    });

    bleManager.onStatus((status) => {
      setServiceModeStatus(prev => ({
        ...prev,
        active: status.service_mode || false,
        gatewayUnlocked: status.gateway_unlocked || false
      }));
    });

    bleManager.onDisconnect(() => {
      setBleConnected(false);
      toast.info("ESP32 disconnected");
    });
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/vehicle/status`);
      setConnectionStatus({ connected: response.data.connected });
      if (response.data.connected && response.data.connection) {
        setVehicleInfo({
          vin: response.data.connection.vin,
          firmware_version: response.data.connection.firmware_version,
        });
      }
    } catch (error) {
      console.error("Failed to check connection status:", error);
    }
  };

  const connectVehicle = async (connectionType = "USB", port = "/dev/ttyUSB0") => {
    setIsConnecting(true);
    try {
      const response = await axios.post(`${API}/vehicle/connect`, null, {
        params: { connection_type: connectionType, port }
      });
      setConnectionStatus({ connected: true });
      setVehicleInfo({
        vin: response.data.connection.vin,
        firmware_version: response.data.connection.firmware_version,
      });
      toast.success("Vehicle connected successfully", {
        description: `VIN: ${response.data.connection.vin}`,
      });
    } catch (error) {
      toast.error("Connection failed", {
        description: error.response?.data?.detail || "Unable to connect to vehicle",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectVehicle = async () => {
    try {
      await axios.post(`${API}/vehicle/disconnect`);
      setConnectionStatus({ connected: false });
      setVehicleInfo(null);
      toast.info("Vehicle disconnected");
    } catch (error) {
      toast.error("Disconnect failed");
    }
  };

  const handleServiceWizardComplete = (status) => {
    setServiceModeStatus({
      active: status.serviceModeActive,
      gatewayUnlocked: status.gatewayUnlocked,
      fakraConnected: status.fakraConnected,
      bleConnected: status.bleConnected
    });
    setBleConnected(status.bleConnected);
    toast.success("Service Mode Plus activated!", {
      description: "All ODIN routines are now available"
    });
  };

  return (
    <div className="app-container">
      <Toaster 
        position="top-right" 
        theme="dark"
        toastOptions={{
          style: {
            background: '#121212',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff',
          },
        }}
      />
      
      {/* Scanlines overlay */}
      <div className="scanlines"></div>
      
      <BrowserRouter>
        <Navigation 
          connectionStatus={connectionStatus} 
          vehicleInfo={vehicleInfo}
          bleConnected={bleConnected}
          serviceModeStatus={serviceModeStatus}
        />
        
        <main className="main-content">
          <Routes>
            <Route 
              path="/" 
              element={<UnifiedDashboard />} 
            />
            <Route 
              path="/can-decoder" 
              element={<CANDecoder />} 
            />
            <Route 
              path="/wiring" 
              element={<WiringMap />} 
            />
            <Route 
              path="/procedures" 
              element={<ServiceProcedures />} 
            />
            <Route 
              path="/scanner" 
              element={<MCUScanner />} 
            />
            <Route 
              path="/service-guide" 
              element={<ServiceModeGuide />} 
            />
            <Route 
              path="/dashboard" 
              element={
                <Dashboard 
                  connectionStatus={connectionStatus}
                  vehicleInfo={vehicleInfo}
                  onConnect={connectVehicle}
                  onDisconnect={disconnectVehicle}
                  isConnecting={isConnecting}
                  serviceModeStatus={serviceModeStatus}
                />
              } 
            />
            <Route 
              path="/service-routines" 
              element={
                <ServiceRoutines 
                  connectionStatus={connectionStatus}
                  serviceModeStatus={serviceModeStatus}
                  bleManager={bleManager}
                  bleConnected={bleConnected}
                  bleLiveData={bleLiveData}
                />
              } 
            />
            <Route 
              path="/diagnostics" 
              element={<Diagnostics connectionStatus={connectionStatus} />} 
            />
            <Route 
              path="/live-data" 
              element={
                <LiveData 
                  connectionStatus={connectionStatus}
                  bleLiveData={bleLiveData}
                  bleConnected={bleConnected}
                />
              } 
            />
            <Route 
              path="/modules" 
              element={<Modules connectionStatus={connectionStatus} />} 
            />
            <Route 
              path="/safety" 
              element={<SafetyInfo />} 
            />
            <Route 
              path="/history" 
              element={<SessionHistory />} 
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </BrowserRouter>
    </div>
  );
}

export default App;
